/** Backend client. An empty base URL uses the same-origin /api proxy. */

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export const API_BASE = BASE_URL;
export const API_V1 = `${BASE_URL}/api/v1`;

export interface ApiErrorPayload {
  error: { code: string; message: string; request_id?: string };
}

export class ApiError extends Error {
  code: string;
  requestId?: string;
  context?: Record<string, unknown>;

  constructor(code: string, message: string, requestId?: string, context?: Record<string, unknown>) {
    super(message);
    this.code = code;
    this.requestId = requestId;
    this.context = context;
  }
}

function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)tcheck_csrf=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

async function parse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  if (!response.ok) {
    const payload = data as ApiErrorPayload;
    throw new ApiError(
      payload?.error?.code ?? 'UNKNOWN',
      payload?.error?.message ?? `HTTP ${response.status}`,
      payload?.error?.request_id,
      (payload?.error as { context?: Record<string, unknown> })?.context,
    );
  }
  return data as T;
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
  return parse<T>(response);
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const csrf = csrfToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return parse<T>(response);
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const csrf = csrfToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  return parse<T>(response);
}

export async function apiDelete<T>(path: string): Promise<T> {
  const csrf = csrfToken();
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: { Accept: 'application/json', ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
    credentials: 'include',
  });
  return parse<T>(response);
}

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
  app_env: string;
}

export interface ReadyResponse {
  status: string;
  database: boolean;
  providers: Record<string, boolean>;
  synthetic_mode: boolean;
  app_env: string;
}

export interface ProviderMetadata {
  name: string;
  version: string;
  modality: string;
  maturity: 'PILOT' | 'PRODUCTION_CANDIDATE';
  licence: string;
  weights_licence: string;
  weights_source: string;
  template_format: string;
  template_dim: number;
  certified: boolean;
  notes: string;
}

export interface ProvidersResponse {
  providers: Record<string, ProviderMetadata | Record<string, unknown>>;
  synthetic_mode: boolean;
  app_env: string;
}
